/**
 * Offline/Online status indicator component
 */

import React, { useEffect, useState } from 'react';
import { syncService } from '../services/syncService';
import type { SyncProgress } from '../services/syncService';

export const OfflineIndicator: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Subscribe to online status changes
    const unsubscribeOnline = syncService.onOnlineStatusChange(setIsOnline);

    // Subscribe to sync progress
    const unsubscribeProgress = syncService.onSyncProgress((progress) => {
      setSyncProgress(progress);

      // Hide progress after sync completes
      if (!progress.inProgress) {
        setTimeout(() => setSyncProgress(null), 3000);
      }
    });

    // Initial check
    setIsOnline(syncService.isOnline());

    return () => {
      unsubscribeOnline();
      unsubscribeProgress();
    };
  }, []);

  // Don't show anything if online and no sync in progress
  if (isOnline && !syncProgress?.inProgress) {
    return null;
  }

  const getStatusColor = () => {
    if (!isOnline) return 'bg-red-500';
    if (syncProgress?.inProgress) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (syncProgress?.inProgress) return 'Syncing...';
    return 'Online';
  };

  const getStatusIcon = () => {
    if (!isOnline) {
      return (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
          />
        </svg>
      );
    }

    if (syncProgress?.inProgress) {
      return (
        <svg
          className="w-5 h-5 animate-spin"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      );
    }

    return (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
        />
      </svg>
    );
  };

  return (
    <div className="fixed bottom-20 lg:bottom-4 right-4 z-50">
      <div
        className={`${getStatusColor()} text-white rounded-lg shadow-lg transition-all duration-300 ${
          showDetails ? 'w-64' : 'w-auto'
        }`}
      >
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center space-x-2 px-4 py-2 w-full"
        >
          {getStatusIcon()}
          <span className="font-medium">{getStatusText()}</span>
          {syncProgress && syncProgress.total > 0 && (
            <span className="text-sm">
              ({syncProgress.completed}/{syncProgress.total})
            </span>
          )}
        </button>

        {showDetails && syncProgress && (
          <div className="px-4 pb-3 border-t border-white/20">
            <div className="mt-2 text-sm">
              <div className="flex justify-between">
                <span>Total items:</span>
                <span>{syncProgress.total}</span>
              </div>
              <div className="flex justify-between">
                <span>Completed:</span>
                <span>{syncProgress.completed}</span>
              </div>
              {syncProgress.failed > 0 && (
                <div className="flex justify-between text-red-200">
                  <span>Failed:</span>
                  <span>{syncProgress.failed}</span>
                </div>
              )}
            </div>

            {syncProgress.total > 0 && (
              <div className="mt-2">
                <div className="bg-white/20 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-white h-full transition-all duration-300"
                    style={{
                      width: `${(syncProgress.completed / syncProgress.total) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {!isOnline && (
        <div className="mt-2 bg-gray-800 text-white text-sm rounded-lg px-3 py-2 shadow-lg">
          <p>
            You're working offline. Changes will sync when connection is
            restored.
          </p>
        </div>
      )}
    </div>
  );
};
